import React from 'react';
import {
	TextField,
	MenuItem,
	Grid,
	FormControlLabel,
	Checkbox,
	Typography,
	Button,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import { OpeningHoursDayType, OpeningHourObject } from 'common/types';
import moment from 'moment-timezone';
import TimePicker from 'components/TimePicker';
import Delete from '@mui/icons-material/Delete';
import classNames from 'classnames';
import { useTranslation } from 'services/localization/useTranslation';

import { dayTypeOptions } from './utils';
import { getDayTypeTranslation } from 'common/utils/translations';

interface Props {
	value: OpeningHourObject;
	onChange: (value: OpeningHourObject) => void;
	onDelete: () => void;
	reservedDayTypes: OpeningHoursDayType[];
}

const OpeningHoursDefaultRow = (props: Props) => {
	const classes = useStyles();
	const { t } = useTranslation();
	const { onChange, onDelete, reservedDayTypes } = props;
	const { dayType, openTime, closeTime, isClosed } = props.value;

	const validDayTypes = dayTypeOptions.filter(
		(dt) => !reservedDayTypes.includes(dt) || dt === dayType,
	);

	const handleDayTypeChange = (e: React.ChangeEvent<{}>) => {
		const newDayType = (e.target as any).value as OpeningHoursDayType;
		onChange({
			...props.value,
			dayType: newDayType,
		});
	};

	const handleOpenTimeChange = (value: string) => {
		onChange({
			...props.value,
			openTime: value,
		});
	};

	const handleCloseTimeChange = (value: string) => {
		onChange({
			...props.value,
			closeTime: value,
		});
	};

	const handleToggleIsClosed = () => {
		onChange({
			...props.value,
			isClosed: !isClosed,
		});
	};

	const incosistentOpenTime = moment(openTime, 'HH:mm').isAfter(moment(closeTime, 'HH:mm'));
	const incosistentCloseTime = moment(closeTime, 'HH:mm').isBefore(moment(openTime, 'HH:mm'));

	return (
		<Grid container alignItems="flex-end" spacing={1}>
			<Grid item xs={10} sm={3} className={classes.smallTopOrder}>
				<TextField
					className={classes.textField}
					select
					color="primary"
					variant="outlined"
					value={dayType}
					onChange={handleDayTypeChange}
					SelectProps={{
						classes: {
							select: classes.select,
						},
					}}
				>
					{validDayTypes.map((option) => (
						<MenuItem key={option} value={option}>
							{getDayTypeTranslation(option, t)}
						</MenuItem>
					))}
				</TextField>
			</Grid>
			<Grid item xs={4} sm={2}>
				<Typography variant="body2">{t('common:times.opens', 'Opens')}</Typography>
				<TimePicker
					onChange={handleOpenTimeChange}
					selectedDate={moment(openTime, 'HH:mm').toISOString()}
					noMinDate={true}
					outlined={true}
					disabled={isClosed}
					error={incosistentOpenTime}
				/>
			</Grid>
			<Grid item xs={4} sm={2}>
				<Typography variant="body2">{t('common:times.closes', 'Closes')}</Typography>
				<TimePicker
					onChange={handleCloseTimeChange}
					selectedDate={moment(closeTime, 'HH:mm').toISOString()}
					noMinDate={true}
					outlined={true}
					disabled={isClosed}
					error={incosistentCloseTime}
				/>
			</Grid>
			<Grid item xs={4} sm={3} container justifyContent="flex-end">
				<FormControlLabel
					control={<Checkbox color="primary" checked={isClosed} onChange={handleToggleIsClosed} />}
					label={t('common:times.closed', 'Closed')}
				/>
			</Grid>
			<Grid item xs={2} sm={2} className={classes.smallTopOrder}>
				<Button onClick={onDelete}>
					<Delete className={classNames(classes.removeBtn, classes.icon)} />
				</Button>
			</Grid>
		</Grid>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		textField: {
			width: '100%',
		},
		removeBtn: {
			display: 'block',
			width: 200,
			padding: 8,
		},
		smallTopOrder: {
			[theme.breakpoints.down('sm')]: {
				order: -1,
			},
		},
		icon: {
			width: 40,
			height: 40,
			color: theme.palette.common.black,
		},
		select: {
			padding: 10,
			paddingRight: 32,
		},
	}),
);

export default OpeningHoursDefaultRow;
